import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Award, BadgeCheck, Briefcase, Bug, Building2, Compass, FileUser, Fingerprint, FolderKanban, History, LayoutDashboard, ListChecks, Menu as MenuIcon, Receipt, Scale, ScrollText, Smartphone, Sparkles, UserCog, UserRound, UserSearch, Users, Wallet } from 'lucide-react';
import { useMe } from '@/api/auth';
import { AREA_HOME, areaOfPath, areasOf, type Area } from '@/auth/roles';
import { Sheet } from '@/components/ui/Dialog';
import { IconButton } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { MessagesLink, NotificationBell } from '@/components/domain/Notifications';
import { useLedTeams } from '@/components/domain/Teams';
import { MembershipBanner } from '@/components/domain/Membership';
import { registerNavigate } from '@/lib/navigation';
import { Brand } from './Brand';
import { AreaSwitcher, OrgSwitcher } from './Switchers';
import { UserMenu } from './UserMenu';

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(query).matches === true);
  useEffect(() => {
    const m = window.matchMedia?.(query);
    if (!m) return;
    const on = () => setMatches(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return matches;
}

interface NavItem { to: string; label: string; icon: ReactNode; end?: boolean }

function useNavItems(area: Area | null): NavItem[] {
  const { t } = useTranslation();
  const leadsTeam = useLedTeams().length > 0;
  // The tester's own membership is listed while the platform asks testers for one.
  const testerMembership = useMe().data?.memberships?.some((m) => m.audience === 'Tester') ?? false;
  switch (area) {
    case 'customer':
      return [
        { to: '/projects', label: t('nav.projects'), icon: <FolderKanban /> },
        { to: '/talent', label: t('nav.talent'), icon: <UserSearch /> },
        { to: '/org/billing', label: t('nav.billing'), icon: <Receipt /> },
        { to: '/org/membership', label: t('nav.membership'), icon: <BadgeCheck /> },
        { to: '/org/disputes', label: t('nav.disputes'), icon: <Scale /> },
        { to: '/org/logs', label: t('nav.logs'), icon: <ScrollText /> },
        { to: '/org/settings', label: t('nav.organization'), icon: <Building2 /> },
      ];
    case 'lead':
      return [
        ...(leadsTeam ? [{ to: '/team-panel', label: t('nav.teamPanel'), icon: <LayoutDashboard /> }] : []),
        { to: '/communities', label: t('nav.communities'), icon: <Users /> },
      ];
    case 'tester':
      return [
        { to: '/my/assignments', label: t('nav.assignments'), icon: <ListChecks /> },
        { to: '/jobs', label: t('nav.jobs'), icon: <Briefcase /> },
        { to: '/my/earnings', label: t('nav.earnings'), icon: <Wallet /> },
        ...(testerMembership ? [{ to: '/my/membership', label: t('nav.myMembership'), icon: <BadgeCheck /> }] : []),
        { to: '/teams', label: t('nav.teams'), icon: <Users /> },
        { to: '/my/exploratory/new', label: t('nav.exploratory'), icon: <Compass /> },
        { to: '/my/bugs', label: t('nav.myBugs'), icon: <Bug /> },
        { to: '/my/disputes', label: t('nav.myDisputes'), icon: <Scale /> },
        { to: '/my/reputation', label: t('nav.reputation'), icon: <Award /> },
        { to: '/my/profile', label: t('nav.profile'), icon: <UserRound /> },
        { to: '/my/cv', label: t('nav.cv'), icon: <FileUser /> },
        { to: '/my/devices', label: t('nav.devices'), icon: <Smartphone /> },
        { to: '/my/activity', label: t('nav.activity'), icon: <History /> },
      ];
    case 'admin':
      return [
        { to: '/admin', label: t('nav.adminOverview'), icon: <LayoutDashboard />, end: true },
        { to: '/admin/users', label: t('nav.adminUsers'), icon: <UserCog /> },
        { to: '/admin/fraud', label: t('nav.adminFraud'), icon: <Fingerprint /> },
        { to: '/admin/logs', label: t('nav.allLogs'), icon: <ScrollText /> },
        { to: '/admin/billing', label: t('nav.payments'), icon: <Wallet /> },
        { to: '/admin/memberships', label: t('nav.memberships'), icon: <BadgeCheck /> },
        { to: '/admin/disputes', label: t('nav.disputes'), icon: <Scale /> },
        { to: '/admin/errors', label: t('nav.adminErrors'), icon: <Bug /> },
      ];
    default:
      return [{ to: '/welcome', label: t('nav.getStarted'), icon: <Sparkles /> }];
  }
}

function SidebarContent({ onNavigate, showBell = false }: { onNavigate?: () => void; showBell?: boolean }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { pathname } = useLocation();
  const areas = areasOf(me);
  const pathArea = areaOfPath(pathname);
  const area = pathArea && areas.includes(pathArea) ? pathArea : (areas[0] ?? null);
  const items = useNavItems(area);

  return (
    <div className="flex h-full flex-col gap-4 p-3">
      <div className="flex items-center justify-between gap-2 px-1.5 pt-1">
        <Brand to={area ? AREA_HOME[area] : '/'} />
        {showBell && <span className="flex items-center gap-0.5"><MessagesLink /><NotificationBell /></span>}
      </div>
      <AreaSwitcher areas={areas} />
      {area === 'customer' && <OrgSwitcher />}
      <nav aria-label={t('shell.mainNav')} className="flex flex-col gap-0.5" data-nav="side">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-[var(--radius-ctl)] px-2.5 py-1.5 text-sm font-medium transition-colors [&>svg]:size-4 [&>svg]:shrink-0',
                isActive ? 'bg-accent-soft text-accent-text' : 'text-ink-2 hover:bg-surface-3 hover:text-ink',
              )
            }
          >
            {item.icon}
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto border-t border-line pt-3">
        <UserMenu />
      </div>
    </div>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Toasts (outside the router) can link into the app without reloading it.
  useEffect(() => registerNavigate((to) => void navigate(to)), [navigate]);
  const [open, setOpen] = useState(false);
  // One bell (and one live subscription) at a time: in the sidebar on wide screens, in the top bar otherwise.
  const desktop = useMediaQuery('(min-width: 1024px)');
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[15rem_minmax(0,1fr)]">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2">
        {t('shell.skipToContent')}
      </a>
      <aside className="sticky top-0 hidden h-dvh border-r border-line bg-surface lg:block">
        <SidebarContent showBell={desktop} />
      </aside>
      <header className="sticky top-0 z-30 flex h-12 items-center gap-2 border-b border-line bg-surface/95 px-3 backdrop-blur lg:hidden">
        <IconButton label={t('shell.openMenu')} onClick={() => setOpen(true)}>
          <MenuIcon className="size-5" />
        </IconButton>
        <Brand />
        <div className="ml-auto flex items-center gap-1">
          {!desktop && <><MessagesLink /><NotificationBell /></>}
          <UserMenu compact />
        </div>
      </header>
      <Sheet open={open} onOpenChange={setOpen} title={t('shell.mainNav')}>
        <SidebarContent onNavigate={() => setOpen(false)} />
      </Sheet>
      <main id="main" className="min-w-0">
        <MembershipBanner />
        <Outlet />
      </main>
    </div>
  );
}
